// Material-UI版 従業員追加モーダル

import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import {
  AddEmployeeFormData,
  CompanyEmployeeRole,
  PasswordOption,
} from '../../types/employee';

// バリデーションスキーマ
const addEmployeeSchema = yup.object({
  fullName: yup
    .string()
    .required('氏名は必須です')
    .max(50, '氏名は50文字以内で入力してください')
    .trim(),
  email: yup
    .string()
    .required('メールアドレスは必須です')
    .email('正しいメールアドレス形式で入力してください')
    .max(255, 'メールアドレスは255文字以内で入力してください'),
  department: yup
    .string()
    .max(50, '部署名は50文字以内で入力してください')
    .trim(),
  jobTitle: yup.string().max(50, '役職名は50文字以内で入力してください').trim(),
  role: yup
    .string()
    .required('権限を選択してください')
    .oneOf(Object.values(CompanyEmployeeRole), '正しい権限を選択してください'),
  passwordOption: yup
    .string()
    .required('パスワード設定方法を選択してください')
    .oneOf(
      Object.values(PasswordOption),
      '正しいパスワード設定方法を選択してください'
    ),
  password: yup.string().when('passwordOption', {
    is: PasswordOption.MANUAL,
    then: (schema) =>
      schema
        .required('パスワードは必須です')
        .min(8, 'パスワードは8文字以上で入力してください')
        .matches(
          /^(?=.*[a-zA-Z])(?=.*\d)/,
          'パスワードは英字と数字を含む必要があります'
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
});

interface AddEmployeeModalMUIProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AddEmployeeFormData) => Promise<void>;
}

export const AddEmployeeModalMUI: React.FC<AddEmployeeModalMUIProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<AddEmployeeFormData>({
    resolver: yupResolver(addEmployeeSchema) as any,
    defaultValues: {
      fullName: '',
      email: '',
      department: '',
      jobTitle: '',
      role: CompanyEmployeeRole.EMPLOYEE,
      passwordOption: PasswordOption.AUTO,
      password: '',
    },
  });

  const passwordOption = watch('passwordOption');

  const handleFormSubmit = async (data: AddEmployeeFormData) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // passwordOptionがautoの場合はpasswordフィールドを除外
      const submitData = { ...data };
      if (data.passwordOption === PasswordOption.AUTO) {
        delete submitData.password;
      }

      await onSubmit(submitData);
      reset();
      onClose();
    } catch (error) {
      console.error('従業員追加エラー:', error);
      setSubmitError(
        error instanceof Error ? error.message : '従業員の追加に失敗しました'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      setSubmitError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          width: '100%',
          maxWidth: '720px',
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              従業員を追加
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <form onSubmit={handleSubmit(handleFormSubmit as any)}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}

          {/* 基本情報セクション */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon
                sx={{ fontSize: 20, color: 'text.secondary', mr: 1 }}
              />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                基本情報
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Controller
                name="fullName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="氏名"
                    required
                    error={!!errors.fullName}
                    helperText={errors.fullName?.message}
                    disabled={isSubmitting}
                    placeholder="例: 田中 太郎"
                  />
                )}
              />

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="メールアドレス"
                    type="email"
                    required
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isSubmitting}
                    placeholder="例: tanaka@company.com"
                  />
                )}
              />
            </Box>
          </Box>

          {/* 所属情報セクション */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon
                sx={{ fontSize: 20, color: 'text.secondary', mr: 1 }}
              />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                所属情報
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Controller
                name="department"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="部署"
                    error={!!errors.department}
                    helperText={errors.department?.message}
                    disabled={isSubmitting}
                    placeholder="例: 営業部"
                  />
                )}
              />

              <Controller
                name="jobTitle"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="役職"
                    error={!!errors.jobTitle}
                    helperText={errors.jobTitle?.message}
                    disabled={isSubmitting}
                    placeholder="例: 主任"
                  />
                )}
              />
            </Box>
          </Box>

          {/* 権限設定セクション */}
          <Box sx={{ mb: 3 }}>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <FormControl error={!!errors.role} disabled={isSubmitting}>
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                    権限レベル *
                  </FormLabel>
                  <RadioGroup {...field}>
                    <FormControlLabel
                      value={CompanyEmployeeRole.COMPANY_ADMIN}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            企業管理者
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            全機能へのアクセス、従業員管理、削除時は全従業員が連動削除
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value={CompanyEmployeeRole.HR}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            人事担当者
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            企業管理者と同等の権限、削除時は本人のみ削除（連動なし）
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value={CompanyEmployeeRole.EMPLOYEE}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            一般従業員
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            閲覧権限のみ、自己削除不可
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                  {errors.role && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {errors.role.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Box>

          {/* パスワード設定セクション */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LockIcon sx={{ fontSize: 20, color: 'text.secondary', mr: 1 }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                パスワード設定
              </Typography>
            </Box>

            <Controller
              name="passwordOption"
              control={control}
              render={({ field }) => (
                <FormControl
                  error={!!errors.passwordOption}
                  disabled={isSubmitting}
                >
                  <RadioGroup {...field}>
                    <FormControlLabel
                      value={PasswordOption.AUTO}
                      control={<Radio />}
                      label="自動生成（システムが仮パスワードを生成）"
                    />
                    <FormControlLabel
                      value={PasswordOption.MANUAL}
                      control={<Radio />}
                      label="手動設定（パスワードを指定）"
                    />
                  </RadioGroup>
                  {errors.passwordOption && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {errors.passwordOption.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            {passwordOption === PasswordOption.MANUAL && (
              <Box sx={{ mt: 2 }}>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="パスワード"
                      type="password"
                      required
                      error={!!errors.password}
                      helperText={
                        errors.password?.message ||
                        '8文字以上、英字と数字を含む'
                      }
                      disabled={isSubmitting}
                      placeholder="パスワードを入力"
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        </form>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          variant="outlined"
          sx={{ mr: 1 }}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit(handleFormSubmit as any)}
          disabled={isSubmitting}
          variant="contained"
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <PersonIcon />
            )
          }
        >
          {isSubmitting ? '追加中...' : '従業員を追加'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
